import AttachmentFileIcon from 'ext-ui/svg/icon/AttachmentFileIcon'
import CheckCircleFilledIcon from 'ext-ui/svg/icon/CheckCircleFilledIcon'
import React, { useState } from 'react'
import Gmail2026 from 'src/assets/svgs/Gmail2026.svg'
import ReceiptEvidenceCollapse from 'src/assets/svgs/receiptEvidenceCollapse.svg'
import ReceiptEvidenceDelete from 'src/assets/svgs/receiptEvidenceDelete.svg'
import ReceiptEvidenceExternalLink from 'src/assets/svgs/receiptEvidenceExternalLink.svg'
import ReceiptEvidencePlus from 'src/assets/svgs/receiptEvidencePlus.svg'
import SvgDownload from 'src/components/Svgs/SvgDownload'
import { useTranslate } from 'src/localization/Trns'
import {
  AttachmentUploadType,
  ReceiptAttachmentModel,
} from 'src/models/receiptAttachment.models'
import {
  ReceiptRecoveryCase,
  ReceiptRecoveryLifecycleState,
} from 'src/models/transaction.models'

import style from './ReceiptEvidencePanel.style'

export interface ReceiptEvidenceSummary {
  amountCents?: number
  merchantName?: string
  date?: string
  submittedAt?: string
  submittedByDisplayName?: string
}

export interface ReceiptEvidence {
  receipt?: ReceiptEvidenceSummary
  transaction?: ReceiptEvidenceSummary
  currency?: string
}

interface ReceiptEvidencePanelProps {
  receiptRecoveryCase?: ReceiptRecoveryCase
  receiptAttachment: ReceiptAttachmentModel
  receiptEvidence?: ReceiptEvidence
  onDelete?: (attachmentId: string) => void
  canDelete?: boolean
}

const formatDate = (value?: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return undefined

  return `${match[2]}/${match[3]}/${match[1].slice(-2)}`
}

const formatAmount = (amountCents?: number, currency = 'USD'): string => {
  if (amountCents === undefined) return '—'

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amountCents / 100)
}

const summaryValue = (
  summary: ReceiptEvidenceSummary | undefined,
  currency: string,
): string => {
  if (!summary) return '—'

  return [
    formatAmount(summary.amountCents, currency),
    summary.merchantName,
    formatDate(summary.date),
  ]
    .filter((value) => value !== undefined)
    .join(' · ')
}

const filenameFromAttachment = (
  attachment: ReceiptAttachmentModel,
): string | undefined => {
  try {
    const url = new URL(attachment.urls.original)
    const filename = url.pathname.split('/').filter(Boolean).pop()
    const decodedFilename = filename ? decodeURIComponent(filename) : undefined
    return decodedFilename?.match(/\.[a-zA-Z0-9]+$/)
      ? decodedFilename
      : undefined
  } catch {
    return undefined
  }
}

type ReceiptEvidenceActionIconName =
  | 'external-link'
  | 'delete'
  | 'download'
  | 'plus'
  | 'collapse'

const ReceiptEvidenceActionIcon: React.FC<{
  name: ReceiptEvidenceActionIconName
  src?: string
}> = ({ name, src }) => (
  <span
    className={`ReceiptEvidencePanel--action-icon ReceiptEvidencePanel--action-icon-${name}`}
    data-testid={'receipt-evidence-action-icon'}
    aria-hidden={'true'}
  >
    {name === 'download' ? (
      <SvgDownload fill={'currentColor'} viewBox={'0 0 24 24'} />
    ) : (
      <img src={src} alt={''} />
    )}
  </span>
)

const ReceiptEvidencePanel: React.FC<ReceiptEvidencePanelProps> = ({
  receiptRecoveryCase,
  receiptAttachment,
  receiptEvidence,
  onDelete,
  canDelete = true,
}): React.ReactElement => {
  const translate = useTranslate()
  const [isZoomed, setIsZoomed] = useState(false)
  const fileName =
    receiptRecoveryCase?.recoveredFileName ??
    filenameFromAttachment(receiptAttachment) ??
    translate('Receipt')
  const originalUrl = receiptAttachment.urls.original
  const attachmentId = receiptAttachment.id
  const isDeleteEnabled =
    canDelete && attachmentId !== undefined && onDelete !== undefined
  const previewUrl = receiptAttachment.urls.main ?? originalUrl
  const isImage = receiptAttachment.contentType?.startsWith('image/')
  const currency = receiptEvidence?.currency ?? 'USD'
  const receiptSummary = receiptEvidence?.receipt
  const transactionSummary = receiptEvidence?.transaction
  const hasComparableAmounts =
    receiptSummary?.amountCents !== undefined &&
    transactionSummary?.amountCents !== undefined
  const difference = hasComparableAmounts
    ? Math.abs(receiptSummary.amountCents! - transactionSummary.amountCents!)
    : undefined
  const submittedAt = formatDate(
    receiptSummary?.submittedAt ??
      receiptRecoveryCase?.lastAgentRunAt ??
      receiptAttachment.createdAt,
  )
  const submittedBy =
    receiptSummary?.submittedByDisplayName ??
    (receiptRecoveryCase?.recoveredFrom
      ? translate('Receipt Agent')
      : undefined)
  const isHumanVerified =
    receiptRecoveryCase?.lifecycleState ===
    ReceiptRecoveryLifecycleState.ReceiptReconciled
  const isInAppUpload =
    receiptAttachment.uploadType === AttachmentUploadType.APP
  const isRecoveredFromGmail = receiptRecoveryCase?.recoveredFrom === 'GMAIL'

  return (
    <section
      css={style}
      className={'ReceiptEvidencePanel'}
      aria-label={translate('Receipt evidence')}
      data-testid={'receipt-evidence-panel'}
    >
      <div className={'ReceiptEvidencePanel--action-bar'}>
        <div className={'ReceiptEvidencePanel--action-group'}>
          {previewUrl ? (
            <a
              className={'ReceiptEvidencePanel--icon-button'}
              href={previewUrl}
              target={'_blank'}
              rel={'noopener noreferrer'}
              aria-label={translate('Open receipt in a new tab')}
              data-testid={'receipt-evidence-open'}
            >
              <ReceiptEvidenceActionIcon
                name={'external-link'}
                src={ReceiptEvidenceExternalLink}
              />
            </a>
          ) : (
            <button
              className={'ReceiptEvidencePanel--icon-button'}
              type={'button'}
              disabled
              aria-label={translate('Open receipt in a new tab')}
              data-testid={'receipt-evidence-open'}
            >
              <ReceiptEvidenceActionIcon
                name={'external-link'}
                src={ReceiptEvidenceExternalLink}
              />
            </button>
          )}
          <button
            className={'ReceiptEvidencePanel--icon-button'}
            type={'button'}
            disabled={!isDeleteEnabled}
            onClick={() => {
              if (isDeleteEnabled && attachmentId) {
                onDelete?.(attachmentId)
              }
            }}
            aria-label={translate('Delete receipt')}
            data-testid={'receipt-evidence-delete'}
          >
            <ReceiptEvidenceActionIcon
              name={'delete'}
              src={ReceiptEvidenceDelete}
            />
          </button>
          {originalUrl ? (
            <a
              className={'ReceiptEvidencePanel--icon-button'}
              href={originalUrl}
              download={fileName}
              aria-label={translate('Download receipt')}
              data-testid={'receipt-evidence-download'}
            >
              <ReceiptEvidenceActionIcon name={'download'} />
            </a>
          ) : (
            <button
              className={'ReceiptEvidencePanel--icon-button'}
              type={'button'}
              disabled
              aria-label={translate('Download receipt')}
              data-testid={'receipt-evidence-download'}
            >
              <ReceiptEvidenceActionIcon name={'download'} />
            </button>
          )}
        </div>
        <div className={'ReceiptEvidencePanel--action-group'}>
          <button
            className={'ReceiptEvidencePanel--icon-button'}
            type={'button'}
            onClick={() => setIsZoomed(true)}
            aria-label={translate('Zoom receipt')}
            aria-pressed={isZoomed}
          >
            <ReceiptEvidenceActionIcon
              name={'plus'}
              src={ReceiptEvidencePlus}
            />
          </button>
          <button
            className={'ReceiptEvidencePanel--icon-button'}
            type={'button'}
            onClick={() => setIsZoomed(false)}
            aria-label={translate('Zoom receipt out')}
            aria-pressed={!isZoomed}
          >
            <ReceiptEvidenceActionIcon
              name={'collapse'}
              src={ReceiptEvidenceCollapse}
            />
          </button>
        </div>
      </div>

      <div
        className={'ReceiptEvidencePanel--preview'}
        data-testid={'receipt-evidence-preview'}
      >
        {previewUrl ? (
          isImage ? (
            <img
              className={`ReceiptEvidencePanel--preview-image${
                isZoomed ? ' isZoomed' : ''
              }`}
              src={previewUrl}
              alt={translate('Receipt: ${filename}', {
                filename: fileName,
              })}
            />
          ) : (
            <iframe
              className={'ReceiptEvidencePanel--preview-document'}
              src={previewUrl}
              title={translate('Receipt: ${filename}', {
                filename: fileName,
              })}
            />
          )
        ) : (
          <div className={'ReceiptEvidencePanel--unavailable'}>
            <AttachmentFileIcon width={24} height={24} aria-hidden={'true'} />
            <span>
              {translate('The recovered document is not available to preview.')}
            </span>
          </div>
        )}
      </div>

      <div className={'ReceiptEvidencePanel--divider'} />

      <dl className={'ReceiptEvidencePanel--comparison'}>
        <div>
          <dt>{translate('Receipt on file')}</dt>
          <dd>{summaryValue(receiptSummary, currency)}</dd>
        </div>
        <div>
          <dt>{translate('This transaction')}</dt>
          <dd>{summaryValue(transactionSummary, currency)}</dd>
        </div>
        <div>
          <dt>{translate('Difference')}</dt>
          <dd className={'ReceiptEvidencePanel--difference'}>
            {formatAmount(difference, currency)}
          </dd>
        </div>
      </dl>

      <div className={'ReceiptEvidencePanel--divider'} />

      <div className={'ReceiptEvidencePanel--metadata'}>
        {submittedAt && (
          <span>
            {translate('Submitted on ${date}${by}', {
              date: submittedAt,
              by: submittedBy ? ` by ${submittedBy}` : '',
            })}
          </span>
        )}
        {isInAppUpload && <span>{translate('· In-app upload')}</span>}
        {isRecoveredFromGmail && (
          <span className={'ReceiptEvidencePanel--provenance'}>
            {translate('· Found through Gmail')}
            <img src={Gmail2026} alt={translate('Gmail')} />
          </span>
        )}
        {isHumanVerified && (
          <span className={'ReceiptEvidencePanel--verified'}>
            {translate('Verified')}
            <CheckCircleFilledIcon aria-hidden={'true'} />
          </span>
        )}
      </div>
    </section>
  )
}

export default ReceiptEvidencePanel
